import React, { useMemo } from "react";
import "../../../styles/components/management/Rota/RotaCalendar.css";

export default function RotaCalendar({
  bookings = [],
  assignments = [],
  users = [],
  units = [],
  properties = [],
  selectedProperty,
  selectedUser,
  dateRange,
  openModal,
  colorSettings = {},
  scrollRef,
}) {
  const dateList = useMemo(() => {
    if (!dateRange?.start || !dateRange?.end) return [];
    const start = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    const list = [];
    while (start <= end) {
      list.push(new Date(start));
      start.setDate(start.getDate() + 1);
    }
    return list;
  }, [dateRange]);

  const unitsToShow = useMemo(() => {
    return selectedProperty === "all"
      ? units
      : units.filter((u) => u.PROPERTY_ID === selectedProperty);
  }, [units, selectedProperty]);

  const bookingsByUnit = useMemo(() => {
    const map = {};
    for (const b of bookings) {
      if (!map[b.UNIT_NAME]) map[b.UNIT_NAME] = [];
      map[b.UNIT_NAME].push(b);
    }
    return map;
  }, [bookings]);

  const groupedUnits = useMemo(() => {
    return unitsToShow.reduce((acc, unit) => {
      const prop = properties.find((p) => p.PROPERTY_ID === unit.PROPERTY_ID);
      if (prop) {
        if (!acc[prop.PROPERTY_NAME]) acc[prop.PROPERTY_NAME] = [];
        acc[prop.PROPERTY_NAME].push(unit);
      }
      return acc;
    }, {});
  }, [unitsToShow, properties]);

  const flatUnitList = useMemo(() => {
    return Object.entries(groupedUnits).flatMap(([propertyName, units]) =>
      units.map((unit, index) => ({
        ...unit,
        isFirstOfGroup: index === 0,
        propertyName,
      }))
    );
  }, [groupedUnits]);

  const getAssignmentForDate = (bookingId, date) =>
    assignments.find(
      (a) =>
        String(a.booking_id) === String(bookingId) && a.assign_date === date
    );

  const renderCell = (unit, date) => {
    const dateStr = date.toISOString().slice(0, 10);
    const bookingsForUnit = bookingsByUnit[unit.UNIT_NAME] || [];
    const checkIn = bookingsForUnit.find((b) => b.CHECK_IN_DATE === dateStr);
    const checkOut = bookingsForUnit.find((b) => b.CHECK_OUT_DATE === dateStr);
    const booked = bookingsForUnit.find(
      (b) => dateStr > b.CHECK_IN_DATE && dateStr < b.CHECK_OUT_DATE
    );

    let className = "";
    let booking = null;

    if (checkIn && checkOut && checkIn.BOOK_ID === checkOut.BOOK_ID) {
      className = "cell-both";
      booking = checkOut;
    } else if (checkIn && checkOut) {
      className = "cell-both";
      booking = checkOut;
    } else if (checkIn) {
      className = "cell-checkin";
      booking = checkIn;
    } else if (checkOut) {
      className = "cell-checkout";
      booking = checkOut;
    } else if (booked) {
      className = "cell-booked";
      booking = booked;
    }

    const assignment = booking
      ? getAssignmentForDate(booking.BOOK_ID, dateStr)
      : assignments.find(
          (a) => a.assign_date === dateStr && a.unit_id === unit.UNIT_ID
        );

    const assignedUser = assignment
      ? users.find((u) => String(u.USER_ID) === String(assignment.user_id))
      : null;

    const nextBooking = bookingsForUnit
      .filter((b) => new Date(b.CHECK_IN_DATE) > new Date(dateStr))
      .sort((a, b) => new Date(a.CHECK_IN_DATE) - new Date(b.CHECK_IN_DATE))[0];

    const nextGuestCount = nextBooking
      ? parseInt(nextBooking.NUM_ADULT || 0) + parseInt(nextBooking.NUM_CHILD || 0)
      : null;

    if (!booking && assignment?.is_arrow) {
      className = "cell-arrow";
    }

    if (!booking) {
      const lastBooking = bookingsForUnit
        .filter((b) => new Date(b.CHECK_OUT_DATE) < new Date(dateStr))
        .sort((a, b) => new Date(b.CHECK_OUT_DATE) - new Date(a.CHECK_OUT_DATE))[0];

      if (assignment || lastBooking) {
        booking = lastBooking || null;
        className = "cell-empty";
      } else {
        className = "cell-disabled";
      }
    }

    const handleClick = () => {
      if (className === "cell-disabled") return;

      sessionStorage.setItem("pageScrollY", window.scrollY.toString());
      if (scrollRef?.current) {
        sessionStorage.setItem("innerScrollTop", scrollRef.current.scrollTop.toString());
        sessionStorage.setItem("innerScrollLeft", scrollRef.current.scrollLeft.toString());
      }

      openModal(booking || null, unit, dateStr);
    };

    return (
      <td
        key={unit.UNIT_NAME + dateStr}
        className={`rota-cell ${className}`}
        style={{ cursor: className === "cell-disabled" ? "not-allowed" : "pointer" }}
        onClick={handleClick}
      >
        <div className="cell-content">
          {assignment ? (
            assignment.is_arrow ? (
              <span title="Arrow assignment">⬇️</span>
            ) : assignedUser ? (
              <span>{assignedUser.USER_NAME}</span>
            ) : (
              <span style={{ color: "red" }}>User not found</span>
            )
          ) : null}
          {(className === "cell-checkout" || className === "cell-both") && nextGuestCount ? (
            <div className="guest-count">👤 {nextGuestCount}</div>
          ) : null}
        </div>
      </td>
    );
  };

  return (
    <div className="rota-calendar-wrapper">
      <div className="rota-calendar-body-scroll" ref={scrollRef}>
        <table className="rota-calendar-table">
          <thead>
            <tr className="properties">
              <th className="datelabel" rowSpan={2}>Date</th>
              {Object.entries(groupedUnits).map(([propName, units]) => (
                <th key={propName} colSpan={units.length} className="propertieslabel">
                  {propName}
                </th>
              ))}
            </tr>
            <tr className="units">
              {flatUnitList.map((unit) => (
                <th
                  key={unit.UNIT_NAME}
                  className={`unitslabel ${unit.isFirstOfGroup ? "property-separator" : ""}`}
                >
                  {unit.UNIT_NAME}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dateList.map((date) => (
              <tr key={date.toISOString()}>
                <td className="datelabel">{date.toDateString().slice(0, 10)}</td>
                {flatUnitList.map((unit) => {
                  const cell = renderCell(unit, date);
                  return React.cloneElement(cell, {
                    key: unit.UNIT_NAME + date.toISOString(),
                    className: `${cell.props.className} ${
                      unit.isFirstOfGroup ? "property-separator" : ""
                    }`,
                  });
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
